import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { formatContainerDisplayId } from '../lib/batch';
import { formatQty, isPieceUom, qtyInputStep } from '../lib/quantity';
import { purchaseStatusLabel } from '../lib/purchaseLabels';
import {
  purchaseOrdersApi,
  type PurchaseOrderDetail,
  type PurchaseOrderFormData,
  type PurchaseOrdersMeta,
} from '../services/purchaseOrders';

type PurchaseOrderFormRow = {
  productId: number | '';
  productSearch: string;
  qty: string;
  uomItemId: number | '';
  siteId: number | '';
  waterPercent: string;
  nectarItemId: number | '';
  notes: string;
};

const EMPTY_FORM: PurchaseOrderFormData = {
  poNumber: '',
  supplierId: null,
  status: 'RECEIVED',
  orderDate: '',
  expectedDeliveryDate: null,
  notes: '',
  rows: [],
};

function formatDateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function productLabel(name: string | null | undefined, description: string | null | undefined): string {
  return name ?? description ?? '-';
}

function isBulkHoneyOrPollen(meta: PurchaseOrdersMeta, productId: number | ''): boolean {
  if (productId === '') return false;
  const product = meta.products.find((item) => item.id === Number(productId));
  const category = (product?.categoryLabel ?? '').trim().toLowerCase();
  return product?.kind === 'BULK' && (category === 'honung' || category === 'pollen');
}

function productOptionLabel(product: PurchaseOrdersMeta['products'][number]): string {
  return product.sku ? `${product.sku} - ${product.name}` : product.name;
}

export function ReceivingDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const poId = Number(params.id);
  const [meta, setMeta] = useState<PurchaseOrdersMeta | null>(null);
  const [selected, setSelected] = useState<PurchaseOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<PurchaseOrderFormData>(EMPTY_FORM);
  const [formRows, setFormRows] = useState<PurchaseOrderFormRow[]>([]);
  const [openProductSearchIndex, setOpenProductSearchIndex] = useState<number | null>(null);
  const toast = useToast();
  const { canRead } = useAuth();

  if (!canRead('receiving')) {
    return <Navigate to="/" replace />;
  }
  if (!Number.isFinite(poId) || poId <= 0) {
    return <Navigate to="/receiving" replace />;
  }

  const loadPurchaseOrder = useCallback(async () => {
    try {
      const [metaRes, detail] = await Promise.all([purchaseOrdersApi.meta(), purchaseOrdersApi.get(poId)]);
      setMeta(metaRes);
      setSelected(detail);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda inköp');
      navigate('/receiving');
    } finally {
      setLoading(false);
    }
  }, [navigate, poId, toast]);

  useEffect(() => {
    loadPurchaseOrder();
  }, [loadPurchaseOrder]);

  const totalQty = useMemo(
    () => selected?.rows.reduce((sum, row) => sum + Number(row.qty ?? 0), 0) ?? 0,
    [selected]
  );

  const defaultSiteId = meta?.sites.find((site) => site.name === 'Lager')?.id ?? meta?.sites[0]?.id ?? '';

  const openEdit = () => {
    if (!selected || !meta) return;
    setForm({
      poNumber: selected.poNumber,
      supplierId: selected.supplierId,
      status: 'RECEIVED',
      orderDate: selected.orderDate,
      expectedDeliveryDate: selected.expectedDeliveryDate,
      notes: selected.notes ?? '',
      rows: selected.rows.map((row) => ({
        productId: row.productId,
        description: row.description ?? null,
        qty: Number(row.qty),
        uomItemId: row.uomItemId ?? null,
        siteId: row.siteId ?? null,
        waterPercent: row.waterPercent ?? null,
        nectarItemId: row.nectarItemId ?? null,
        notes: row.notes ?? null,
      })),
    });
    setFormRows(
      selected.rows.map((row) => {
        const product = meta.products.find((item) => item.id === row.productId);
        return {
          productId: row.productId,
          productSearch: product ? productOptionLabel(product) : productLabel(row.productName, row.description),
          qty: formatQty(row.qty, row.uomLabel),
          uomItemId: row.uomItemId ?? '',
          siteId: row.siteId ?? defaultSiteId,
          waterPercent: row.waterPercent == null ? '' : String(row.waterPercent),
          nectarItemId: row.nectarItemId ?? '',
          notes: row.notes ?? '',
        };
      })
    );
    setOpenProductSearchIndex(null);
    setShowForm(true);
  };

  const updateFormRow = (index: number, patch: Partial<PurchaseOrderFormRow>) => {
    setFormRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const handleProductSelect = (index: number, productId: number) => {
    const product = meta?.products.find((item) => item.id === productId);
    if (!product) return;
    updateFormRow(index, {
      productId,
      productSearch: productOptionLabel(product),
      uomItemId: product.defaultUomItemId ?? '',
      waterPercent: '',
      nectarItemId: '',
    });
    setOpenProductSearchIndex(null);
  };

  const handleSave = async () => {
    const rows = formRows
      .filter((row) => row.productId !== '' && row.qty.trim() !== '' && row.siteId !== '')
      .map((row) => ({
        productId: Number(row.productId),
        qty: Number(row.qty),
        uomItemId: row.uomItemId === '' ? null : Number(row.uomItemId),
        siteId: row.siteId === '' ? null : Number(row.siteId),
        waterPercent: row.waterPercent.trim() === '' ? null : Number(row.waterPercent),
        nectarItemId: row.nectarItemId === '' ? null : Number(row.nectarItemId),
        notes: row.notes.trim() || null,
      }))
      .filter((row) => row.productId > 0 && row.qty > 0 && (row.siteId ?? 0) > 0);

    const payload: PurchaseOrderFormData = { ...form, status: 'RECEIVED', rows };
    setSaving(true);
    try {
      await purchaseOrdersApi.update(poId, payload);
      toast.success('Inköp uppdaterat och lager ombokat');
      setShowForm(false);
      await loadPurchaseOrder();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara inköp');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !meta || !selected) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate('/receiving')}
            className="mb-2 rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
          >
            Tillbaka
          </button>
          <h1 className="text-lg font-semibold">{selected.poNumber}</h1>
          <p className="text-sm text-gray-600">{selected.supplierName ?? 'Ingen leverantör'}</p>
        </div>
        <div className="flex gap-2">
          <WithWrite moduleCode="receiving">
            <button
              type="button"
              onClick={openEdit}
              className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
            >
              Redigera
            </button>
          </WithWrite>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 rounded border border-gray-200 bg-white p-4 text-sm">
        <div>
          <div className="text-gray-500">Status</div>
          <div>{purchaseStatusLabel(selected.status)}</div>
        </div>
        <div>
          <div className="text-gray-500">Datum</div>
          <div>{formatDateOnly(selected.orderDate)}</div>
        </div>
        <div>
          <div className="text-gray-500">Leverantör</div>
          <div>{selected.supplierName ?? '-'}</div>
        </div>
        <div>
          <div className="text-gray-500">Total mängd</div>
          <div>{formatQty(totalQty)}</div>
        </div>
        <div className="col-span-2">
          <div className="text-gray-500">Anteckningar</div>
          <div className="whitespace-pre-wrap">{selected.notes ?? '-'}</div>
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="font-medium">Rader</h2>
        <TableWrapper>
          <TableHead>
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">BatchNr</th>
              <th className="px-2 py-1.5 text-left font-medium">Produkt</th>
              <th className="px-2 py-1.5 text-left font-medium">Mängd</th>
              <th className="px-2 py-1.5 text-left font-medium">Enhet</th>
              <th className="px-2 py-1.5 text-left font-medium">Lagerplats</th>
              <th className="px-2 py-1.5 text-left font-medium">Vattenhalt</th>
              <th className="px-2 py-1.5 text-left font-medium">Nektar</th>
              <th className="px-2 py-1.5 text-left font-medium">Anteckningar</th>
            </tr>
          </TableHead>
          <tbody>
            {selected.rows.map((row) => (
              <tr key={row.id} className="border-b border-gray-100">
                <td className="px-2 py-1">{formatContainerDisplayId(row.containerId ?? null, row.containerCode ?? null) || '-'}</td>
                <td className="px-2 py-1">{productLabel(row.productName, row.description)}</td>
                <td className="px-2 py-1">{formatQty(row.qty, row.uomLabel)}</td>
                <td className="px-2 py-1">{row.uomLabel ?? '-'}</td>
                <td className="px-2 py-1">{row.siteName ?? '-'}</td>
                <td className="px-2 py-1">{row.waterPercent == null ? '-' : `${row.waterPercent}%`}</td>
                <td className="px-2 py-1">{row.nectarLabel ?? '-'}</td>
                <td className="px-2 py-1 whitespace-pre-wrap">{row.notes ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </TableWrapper>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <div>
                <h2 className="text-base font-semibold">Redigera inköp</h2>
                <p className="text-sm text-gray-600">När du sparar bokförs inköpet om i lagret.</p>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div>
                  <label className="block text-sm text-gray-600">Inköpsnummer *</label>
                  <input
                    value={form.poNumber}
                    onChange={(event) => setForm((current) => ({ ...current, poNumber: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Leverantör</label>
                  <select
                    value={form.supplierId ?? ''}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        supplierId: event.target.value ? Number(event.target.value) : null,
                      }))
                    }
                    className="w-full"
                  >
                    <option value="">-</option>
                    {meta.suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Datum *</label>
                  <input
                    type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
                    value={form.orderDate}
                    onChange={(event) => setForm((current) => ({ ...current, orderDate: event.target.value }))}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium">Rader</h3>
                  <button
                    type="button"
                    onClick={() =>
                      setFormRows((current) => [
                        ...current,
                        {
                          productId: '',
                          productSearch: '',
                          qty: '',
                          uomItemId: '',
                          siteId: defaultSiteId,
                          waterPercent: '',
                          nectarItemId: '',
                          notes: '',
                        },
                      ])
                    }
                    className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover"
                  >
                    Lägg till rad
                  </button>
                </div>
                <div className="space-y-2">
                  {formRows.map((row, index) => {
                    const selectedUomLabel =
                      meta.uoms.find((uom) => uom.id === (row.uomItemId === '' ? null : row.uomItemId))?.label ?? null;
                    const showBulkFields = isBulkHoneyOrPollen(meta, row.productId);
                    const matchingProducts = meta.products
                      .filter((product) => {
                        const search = row.productSearch.trim().toLowerCase();
                        if (!search) return true;
                        return `${product.sku ?? ''} ${product.name}`.toLowerCase().includes(search);
                      })
                      .slice(0, 12);

                    return (
                      <div key={index} className="grid grid-cols-12 gap-2 rounded border border-gray-200 p-3">
                        <div className="col-span-12 md:col-span-4">
                          <label className="block text-sm text-gray-600">Produkt</label>
                          <div className="relative">
                            <input
                              value={row.productSearch}
                              placeholder="Sök artikelnummer eller namn"
                              onFocus={() => setOpenProductSearchIndex(index)}
                              onChange={(event) => updateFormRow(index, { productId: '', productSearch: event.target.value })}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setOpenProductSearchIndex((current) => (current === index ? null : current));
                                  setFormRows((current) =>
                                    current.map((entry, rowIndex) => {
                                      if (rowIndex !== index || entry.productId === '') return entry;
                                      const product = meta.products.find(
                                        (candidate) => candidate.id === Number(entry.productId)
                                      );
                                      return product
                                        ? { ...entry, productSearch: productOptionLabel(product) }
                                        : entry;
                                    })
                                  );
                                }, 120);
                              }}
                              className="w-full"
                            />
                            {openProductSearchIndex === index && (
                              <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                                {matchingProducts.map((product) => (
                                  <button
                                    key={product.id}
                                    type="button"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      handleProductSelect(index, product.id);
                                    }}
                                    className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                                  >
                                    {productOptionLabel(product)}
                                  </button>
                                ))}
                                {matchingProducts.length === 0 && (
                                  <div className="px-3 py-2 text-sm text-gray-500">Ingen träff</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-span-6 md:col-span-2">
                          <label className="block text-sm text-gray-600">Mängd</label>
                          <input
                            type="number"
                            min="0"
                            step={qtyInputStep(selectedUomLabel)}
                            value={row.qty}
                            onKeyDown={(event) => {
                              if (isPieceUom(selectedUomLabel) && (event.key === '.' || event.key === ',')) {
                                event.preventDefault();
                              }
                            }}
                            onChange={(event) => updateFormRow(index, { qty: event.target.value })}
                            className="w-full"
                          />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                          <label className="block text-sm text-gray-600">Enhet</label>
                          <select
                            value={row.uomItemId}
                            onChange={(event) =>
                              updateFormRow(index, {
                                uomItemId: event.target.value ? Number(event.target.value) : '',
                              })
                            }
                            className="w-full"
                          >
                            <option value="">-</option>
                            {meta.uoms.map((uom) => (
                              <option key={uom.id} value={uom.id}>
                                {uom.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-12 md:col-span-3">
                          <label className="block text-sm text-gray-600">Lagerplats *</label>
                          <select
                            value={row.siteId}
                            onChange={(event) =>
                              updateFormRow(index, { siteId: event.target.value ? Number(event.target.value) : '' })
                            }
                            className="w-full"
                          >
                            <option value="">Välj lagerplats</option>
                            {meta.sites.map((site) => (
                              <option key={site.id} value={site.id}>
                                {site.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-12 md:col-span-1 flex items-end justify-end">
                          <button
                            type="button"
                            onClick={() => setFormRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                            className="rounded border border-red-200 px-2 py-1 text-sm text-red-700 hover:bg-red-50"
                          >
                            Ta bort
                          </button>
                        </div>
                        {showBulkFields && (
                          <>
                            <div className="col-span-12 md:col-span-3">
                              <label className="block text-sm text-gray-600">Vattenhalt %</label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={row.waterPercent}
                                onChange={(event) => updateFormRow(index, { waterPercent: event.target.value })}
                                className="w-full"
                              />
                            </div>
                            <div className="col-span-12 md:col-span-3">
                              <label className="block text-sm text-gray-600">Nektar</label>
                              <select
                                value={row.nectarItemId}
                                onChange={(event) =>
                                  updateFormRow(index, {
                                    nectarItemId: event.target.value ? Number(event.target.value) : '',
                                  })
                                }
                                className="w-full"
                              >
                                <option value="">-</option>
                                {meta.nectars.map((nectar) => (
                                  <option key={nectar.id} value={nectar.id}>
                                    {nectar.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </>
                        )}
                        <div className="col-span-12">
                          <label className="block text-sm text-gray-600">Anteckningar</label>
                          <input
                            value={row.notes}
                            onChange={(event) => updateFormRow(index, { notes: event.target.value })}
                            className="w-full"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600">Anteckningar</label>
                <textarea
                  value={form.notes ?? ''}
                  onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                  className="min-h-[100px] w-full"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                >
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
